import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, UserPlus, ExternalLink, Loader2, RefreshCw, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Advocates page (cleaner rebuild).
 * Shows live advocates + their referrals, with manual attribution UI
 * for cases where `?adv=` tracking didn't fire.
 *
 * Replaces the legacy broken financials-advocate section. Accessible at
 * /admin/advocates (wired via App.js route).
 */
export default function AdminAdvocatesPage() {
  const navigate = useNavigate();
  const [advocates, setAdvocates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachModal, setAttachModal] = useState(null); // {advocate_id}
  const [attachForm, setAttachForm] = useState({ user_id: "", referral_type: "signup", referral_amount: 0, notes: "" });
  const [detailView, setDetailView] = useState(null); // {advocate_id, referrals, waitlist}

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/advocates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAdvocates(data.advocates || data || []);
    } catch (err) {
      toast.error("Could not load advocates: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDetails = async (advocate_id) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/advocates/${advocate_id}/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDetailView(data);
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  const submitManualAttribution = async () => {
    if (!attachForm.user_id) {
      toast.error("user_id required");
      return;
    }
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/admin/advocates/${attachModal.advocate_id}/add-referral`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(attachForm),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Referral attached");
      setAttachModal(null);
      setAttachForm({ user_id: "", referral_type: "signup", referral_amount: 0, notes: "" });
      if (detailView?.advocate?.advocate_id === attachModal.advocate_id) {
        openDetails(attachModal.advocate_id);
      }
    } catch (err) {
      toast.error(String(err.message || err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-advocates-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-lg font-semibold">Advocate Management</h1>
              <p className="text-xs text-gray-500">Live projects + manual attribution</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200"
              data-testid="advocates-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Button
              size="sm"
              onClick={() => navigate("/admin/dashboard")}
              variant="outline"
              data-testid="advocates-back-admin-btn"
            >
              Back to Admin
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading advocates...
          </div>
        ) : advocates.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center" data-testid="advocates-empty">
            <p className="text-gray-500 mb-4">No advocates yet.</p>
            <Button onClick={() => navigate("/admin/dashboard")}>Go to Admin Dashboard to create one</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {advocates.map((a) => (
              <div
                key={a.advocate_id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
                data-testid={`advocate-card-${a.advocate_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{a.name || a.email || a.advocate_id}</p>
                    <p className="text-xs text-gray-500 truncate">{a.email}</p>
                  </div>
                  {a.is_live !== false && (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700">Live</span>
                  )}
                </div>
                <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                  <div>Code: <span className="font-mono text-gray-900">{a.tracking_code || a.advocate_code || "-"}</span></div>
                  <div>Commission: <span className="font-semibold">{a.commission_rate || 15}%</span></div>
                  <div>Approved categories: {(a.approved_categories || a.categories || []).join(", ") || "all"}</div>
                  <div>Manual referrals: <span className="font-semibold">{a.manual_referral_count || 0}</span></div>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openDetails(a.advocate_id)}
                    className="text-xs"
                    data-testid={`advocate-details-btn-${a.advocate_id}`}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Details
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setAttachModal({ advocate_id: a.advocate_id, advocate_name: a.name })}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid={`advocate-attach-btn-${a.advocate_id}`}
                  >
                    <UserPlus className="w-3 h-3 mr-1" /> Attach User
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Manual Attribution Modal */}
      <Dialog open={!!attachModal} onOpenChange={() => setAttachModal(null)}>
        <DialogContent className="max-w-md" data-testid="attach-referral-modal">
          <DialogHeader>
            <DialogTitle>Attach user to {attachModal?.advocate_name || "advocate"}</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Use this when a user signed up without the `?adv=` tracking link but is clearly attributable to this advocate.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700">User ID</label>
              <Input
                value={attachForm.user_id}
                onChange={(e) => setAttachForm({ ...attachForm, user_id: e.target.value })}
                placeholder="usr_..."
                className="mt-1 text-sm"
                data-testid="attach-user-id-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Referral type</label>
              <select
                value={attachForm.referral_type}
                onChange={(e) => setAttachForm({ ...attachForm, referral_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
                data-testid="attach-referral-type-select"
              >
                <option value="signup">Signup</option>
                <option value="waitlist">Waitlist</option>
                <option value="subscription">Subscription</option>
                <option value="booking">Booking</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Amount (if revenue-bearing)</label>
              <Input
                type="number"
                value={attachForm.referral_amount}
                onChange={(e) => setAttachForm({ ...attachForm, referral_amount: parseFloat(e.target.value) || 0 })}
                className="mt-1 text-sm"
                data-testid="attach-amount-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Notes</label>
              <Input
                value={attachForm.notes}
                onChange={(e) => setAttachForm({ ...attachForm, notes: e.target.value })}
                placeholder="Why this attribution?"
                className="mt-1 text-sm"
                data-testid="attach-notes-input"
              />
            </div>
            <Button
              onClick={submitManualAttribution}
              className="w-full"
              data-testid="attach-submit-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Attach Referral
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail View Modal */}
      <Dialog open={!!detailView} onOpenChange={() => setDetailView(null)}>
        <DialogContent className="max-w-2xl" data-testid="advocate-detail-modal">
          <DialogHeader>
            <DialogTitle>{detailView?.advocate?.name || "Advocate"} · Referrals</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <h4 className="text-sm font-semibold mb-2">Tracked referrals ({detailView?.total_referrals || 0})</h4>
              {detailView?.referrals?.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {detailView.referrals.map((r) => (
                    <li key={r.referral_id} className="py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.referred_name || r.referred_email}</span>
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded">{r.referral_type}</span>
                        {r.manually_attributed && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Manual</span>
                        )}
                        {r.referral_amount > 0 && (
                          <span className="text-green-700 font-medium flex items-center gap-0.5">
                            <DollarSign className="w-3 h-3" /> {r.referral_amount}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 mt-0.5">{r.notes}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500">No tracked referrals.</p>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Waitlist signups ({detailView?.total_waitlist || 0})</h4>
              {detailView?.waitlist_signups?.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {detailView.waitlist_signups.slice(0, 20).map((w) => (
                    <li key={w.waitlist_id} className="py-2 text-xs">
                      <span className="font-medium">{w.name || w.email}</span>
                      <span className="text-gray-500 ml-2">{w.category || (w.service_categories || []).join(", ")}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500">None.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
